import {useCallback, useMemo, type Dispatch, type SetStateAction} from 'react'
import type {DecisionCola, DecisionLocal, TarjetaCola} from '@/lib/bateria-cola'

export type ConflictosDeLaCola = {
  irAlPrimerConflicto: () => void
  dejarConflictoAsi: (tarjeta: TarjetaCola) => void
  cambiarPorLaMia: (tarjeta: TarjetaCola) => void
}

export function useConflictosCola({
  tarjetas,
  decisiones,
  setDecisiones,
  irATarjeta,
  avanzar,
  encolar,
}: {
  tarjetas: TarjetaCola[]
  decisiones: Record<string, DecisionLocal>
  setDecisiones: Dispatch<SetStateAction<Record<string, DecisionLocal>>>
  irATarjeta: (tarjeta: TarjetaCola) => void
  avanzar: () => void
  encolar: (decisiones: DecisionCola[]) => void
}): ConflictosDeLaCola {
  const refsEnConflicto = useMemo(
    () =>
      new Set(
        Object.entries(decisiones)
          .filter(([, local]) => local.estado === 'conflicto')
          .map(([ref]) => ref)
      ),
    [decisiones]
  )

  const irAlPrimerConflicto = useCallback(() => {
    const enConflicto = tarjetas.find((tarjeta) => refsEnConflicto.has(tarjeta.ref))
    if (enConflicto) irATarjeta(enConflicto)
  }, [tarjetas, refsEnConflicto, irATarjeta])

  /** Lo tecleado se reemplaza por lo guardado y la tarjeta cuenta como hecha. */
  const dejarConflictoAsi = useCallback(
    (tarjeta: TarjetaCola) => {
      setDecisiones((previas) => {
        const local = previas[tarjeta.ref]
        if (!local) return previas
        return {
          ...previas,
          [tarjeta.ref]: {
            decision: {...local.decision, valor: local.ganador ?? null, gesto: 'opcion'},
            estado: 'enviada',
          },
        }
      })
      avanzar()
    },
    [setDecisiones, avanzar]
  )

  const cambiarPorLaMia = useCallback(
    (tarjeta: TarjetaCola) => {
      const local = decisiones[tarjeta.ref]
      if (!local) return
      const decision: DecisionCola = {...local.decision, forzar: true}
      setDecisiones((previas) => ({...previas, [tarjeta.ref]: {decision, estado: 'pendiente'}}))
      encolar([decision])
      avanzar()
    },
    [decisiones, setDecisiones, encolar, avanzar]
  )

  return {irAlPrimerConflicto, dejarConflictoAsi, cambiarPorLaMia}
}
